import React, { PropsWithChildren } from 'react';
import { useTheme } from '@mui/material/styles';
import { PaperProps, Box, Typography, SxProps, Theme } from '@mui/material';
import { AlertStatus } from '@models/alert';
import { Alert } from '@components/alert/Alert';
import { useGetScreenSize } from '@utils/useGetScreenSize';

type HeaderSizes = 'h6' | 'h5' | 'h3';

type BannerProps = {
  text: string | React.ReactNode;
  type: AlertStatus;
  ctaText?: string;
  ctaUrl?: string;
  ctaFunction?: () => void;
};

interface PanelProps extends PaperProps {
  panelColor?: string;
  sx?: SxProps<Theme>;
  title?: string;
  titleComponent?: React.ReactNode;
  headerSize?: HeaderSizes;
  banner?: BannerProps | BannerProps[];
}

export const Panel: React.FC<PropsWithChildren<PanelProps>> = ({
  sx,
  title,
  headerSize = 'h4',
  titleComponent,
  children,
  banner,
  ...other
}) => {
  const { isMobileScreen, isSmallScreen } = useGetScreenSize();
  const theme = useTheme<Theme>();
  const { palette } = theme;
  const { panelColor = palette.info.main, ...rest } = other;

  const bannerColor = {
    [AlertStatus.Info]: palette.info.main,
    [AlertStatus.Warning]: palette.warning.main,
    [AlertStatus.Incident]: palette.error.main,
    [AlertStatus.Promotion]: palette.success.main,
    [AlertStatus.SiteUpdateAlert]: palette.button.tertiaryDark,
  };

  const isTinyScreen = isMobileScreen || isSmallScreen;

  const banners = banner ? (Array.isArray(banner) ? banner : [banner]) : [];
  // The banner adjacent to the panel body drives the left border color so the two stay visually connected.
  const adjacentBanner = banners[banners.length - 1];

  return (
    <>
      {banners.map((currentBanner, index) => (
        <Alert
          key={index}
          type={currentBanner.type}
          ctaText={currentBanner.ctaText}
          ctaUrl={currentBanner.ctaUrl}
          ctaFunction={currentBanner.ctaFunction}
          sx={{
            padding: { xs: '8px 0', sm: '8px 16px' },
            // Only the topmost banner gets rounded top corners; stacked banners stay flush.
            borderTopRightRadius: index === 0 ? '8px' : '',
            borderTopLeftRadius: index === 0 ? '8px' : '',
          }}
        >
          {currentBanner.text}
        </Alert>
      ))}
      <Box
        sx={[
          {
            backgroundColor: (theme: Theme) =>
              theme.palette.theme === 'lio' ? 'common.white' : theme.palette.panel.base,
            borderRadius: adjacentBanner ? '' : `${theme.shape.borderRadius * 4}px`,
            borderBottomLeftRadius: `${theme.shape.borderRadius * 4}px`,
            borderBottomRightRadius: `${theme.shape.borderRadius * 4}px`,

            borderLeftColor: adjacentBanner ? bannerColor[adjacentBanner.type] : panelColor,
            padding: { xs: 1, sm: 2 },
            borderLeftWidth: isTinyScreen ? '0px' : '4px',
            borderLeftStyle: isTinyScreen ? 'none' : 'solid',
            '@media only screen and (max-width: 600px)': {
              margin: '0 auto',
            },
            boxShadow: theme.shadows[4],
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
          // Applied last so it always wins over caller-provided sx.
          { '--Paper-overlay': 'unset !important' },
        ]}
        {...rest}
      >
        {titleComponent && titleComponent}
        {title && (
          <Typography
            sx={{ padding: { xs: 1, sm: 0 }, paddingBottom: { xs: 0 } }}
            variant={headerSize as HeaderSizes}
            marginBottom={2}
            color='text.main'
          >
            {title}
          </Typography>
        )}
        {children}
      </Box>
    </>
  );
};
